import { Component, inject, OnDestroy, OnInit, output, ElementRef, ViewChild, AfterViewInit, NgZone, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { interval, Subscription } from 'rxjs';
import { DocumentMetadata, WebhookDelivery } from '../../types/document-metadata';
import { webhookTooltip } from '../../utils/webhook-delivery.utils';
import { DocumentStatus, OcrHistoryService } from '../../services/ocr-history.service';
import { Document } from '../../amp-graphql/API';
import { ActivatedRoute, Router } from '@angular/router';
import { DocumentTemplateService } from '../../services/document-template.service';
import { OcrPrimeStore } from '../../store/ocr-prime.store';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatListModule } from '@angular/material/list';
import { MatDividerModule } from '@angular/material/divider';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { OcrHistoryFiltersComponent } from '../ocr-history-filters/ocr-history-filters.component';
import { OcrDocumentViewerPageComponent } from '../../pages/ocr-document-viewer/ocr-document-viewer.component';
import { ResizableContainerComponent } from '../resizable-container/resizable-container.component';
import { parseOcrError } from '../ocr-error/ocr-error.component';

const STATUSES: DocumentStatus[] = ['done', 'processing', 'persisted', 'error'];

interface TabState {
  items: DocumentMetadata[];
  filtered: DocumentMetadata[];
  nextToken: string | null;
  loading: boolean;
  loadingMore: boolean;
}

@Component({
  selector: 'app-ocr-history',
  templateUrl: './ocr-history.component.html',
  styleUrls: ['./ocr-history.component.scss'],
  imports: [
    CommonModule,
    MatProgressSpinnerModule,
    MatListModule,
    MatDividerModule,
    MatTooltipModule,
    MatIconModule,
    MatButtonModule,
    OcrHistoryFiltersComponent,
    OcrDocumentViewerPageComponent,
    ResizableContainerComponent
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  host: {
    'tabindex': '0',
    'style': 'outline: none;',
    '(focus)': 'onContainerFocus()',
    '(keydown)': 'onKeyDown($event)'
  }
})
export class OcrHistoryComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild('infiniteScrollSentinel') set sentinel(element: ElementRef | undefined) {
    if (element) {
      this.observer?.observe(element.nativeElement);
    }
  }

  private observer: IntersectionObserver | undefined;
  private isSentinelVisible = false;
  private ngZone = inject(NgZone);
  /** Dummy clock used to trigger change detection for relative time labels */
  private _clock = Date.now();
  private timeRefreshSub: Subscription | undefined;
  error: any;
  selectedDocument: DocumentMetadata | null = null;
  isCollapsed = false;
  /** Controls filters panel visibility; default collapsed (hidden) */
  showFilters = false;
  /** Emitted when the toolbar Upload button is clicked (dialog opens in the page wrapper) */
  uploadClick = output<void>();
  private documentUpdateSubscription: Subscription | undefined;

  readonly STATUSES = STATUSES;
  readonly pageSize = 50;
  activeTab: DocumentStatus = 'done';
  tabs: Record<DocumentStatus, TabState> = {
    done: this.emptyTab(),
    processing: this.emptyTab(),
    persisted: this.emptyTab(),
    error: this.emptyTab()
  };

  // Filters state (status removed — tabs replace it)
  filters: {
    filename: string;
    startDate: string | null; // ISO yyyy-MM-dd
    endDate: string | null;   // ISO yyyy-MM-dd
    needsAttention: boolean;
  } = {
    filename: '',
    startDate: null,
    endDate: null,
    needsAttention: false
  };

  private ocrHistoryService = inject(OcrHistoryService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private documentTemplateService = inject(DocumentTemplateService);
  readonly store = inject(OcrPrimeStore);

  readonly webhookTooltip = webhookTooltip;

  private emptyTab(): TabState {
    return { items: [], filtered: [], nextToken: null, loading: false, loadingMore: false };
  }

  get activeTabState(): TabState {
    return this.tabs[this.activeTab];
  }

  /**
   * Maps a GraphQL Document to a DocumentMetadata object
   */
  private mapToDocumentMetadata(doc: Document): DocumentMetadata {
    return {
      s3Key: doc.s3Key,
      originalFileName: doc.originalFileName,
      status: doc.status as 'persisted' | 'processing' | 'done' | 'error',
      timestamp: doc.timestamp,
      errorMessage: doc.errorMessage,
      usedTemplateId: doc.usedTemplateId,
      ocrResult: doc.ocrResult ? JSON.parse(doc.ocrResult) : null,
      ocrJudgeResult: (doc as any).ocrJudgeResult ? JSON.parse((doc as any).ocrJudgeResult as string) : null,
      reviewed: (doc as any).reviewed ?? null,
      reviewedAt: (doc as any).reviewedAt ?? null,
      reviewedBy: (doc as any).reviewedBy ?? null,
      documentAnchors: (doc as any).documentAnchors ? JSON.parse((doc as any).documentAnchors as string) : null,
      templateMatchResult: (doc as any).templateMatchResult ? JSON.parse((doc as any).templateMatchResult as string) : null,
      anchorTreeTrace: (doc as any).anchorTreeTrace ? JSON.parse((doc as any).anchorTreeTrace as string) : null,
      webhookDelivery: (doc.webhookDelivery as WebhookDelivery | null | undefined) ?? null
    };
  }

  ngOnInit(): void {
    this.store.loadTemplates();
    this.store.loadStructures();

    this.timeRefreshSub = interval(60_000).subscribe(() => {
      this._clock = Date.now();
    });

    STATUSES.forEach(s => this.loadTab(s, false));
    this.subscribeToDocumentUpdates();

    this.route.queryParams.subscribe(params => {
      const s3Key = params['s3Key'];
      if (s3Key) {
        this.documentTemplateService.getProcessingMetadata(s3Key).subscribe({
          next: (documentData) => {
            if (documentData) {
              this.selectedDocument = documentData;
              if (documentData.status) {
                this.activeTab = documentData.status as DocumentStatus;
              }
              this.isCollapsed = true;
            }
          },
          error: (err) => {
            console.error('Error fetching document by s3Key:', err);
          }
        });
      }
    });
  }

  ngAfterViewInit(): void {
    this.initInfiniteScroll();
  }

  private initInfiniteScroll(): void {
    const options = {
      root: null,
      rootMargin: '100px',
      threshold: 0.1
    };

    this.observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        this.isSentinelVisible = entry.isIntersecting;
        if (this.isSentinelVisible) {
          this.ngZone.run(() => {
            this.loadMoreIfPossible();
          });
        }
      });
    }, options);
  }

  private loadMoreIfPossible(): void {
    if (this.activeTabState.nextToken && this.isSentinelVisible && !this.activeTabState.loadingMore && !this.activeTabState.loading) {
      this.loadMore();
    }
  }

  ngOnDestroy(): void {
    if (this.timeRefreshSub) {
      this.timeRefreshSub.unsubscribe();
    }
    if (this.documentUpdateSubscription) {
      this.documentUpdateSubscription.unsubscribe();
    }
    if (this.observer) {
      this.observer.disconnect();
    }
  }

  loadTab(status: DocumentStatus, append: boolean): void {
    const tab = this.tabs[status];
    if (append) {
      if (!tab.nextToken || tab.loadingMore) return;
      tab.loadingMore = true;
    } else {
      tab.loading = true;
      tab.nextToken = null;
    }
    this.ocrHistoryService
      .getHistory(status, this.pageSize, append ? tab.nextToken : null)
      .subscribe({
        next: (data) => {
          tab.loading = false;
          tab.loadingMore = false;
          if (!data?.listDocuments) return;
          const page = data.listDocuments.items.map(i => this.mapToDocumentMetadata(i));
          tab.items = append ? [...tab.items, ...page] : page;
          tab.nextToken = data.listDocuments.nextToken ?? null;
          tab.filtered = this.applyFilters(tab.items);

          // If current tab is active and more data is needed to fill the screen, auto-load more
          if (status === this.activeTab) {
            this.loadMoreIfPossible();
          }
        },
        error: (error) => {
          tab.loading = false;
          tab.loadingMore = false;
          console.error(`Error fetching ${status} documents:`, error);
        }
      });
  }

  loadMore(): void {
    this.loadTab(this.activeTab, true);
  }

  tabCountLabel(status: DocumentStatus): string {
    const tab = this.tabs[status];
    return tab.nextToken ? `${tab.items.length}+` : `${tab.items.length}`;
  }

  onTabChange(index: number): void {
    const newStatus = STATUSES[index];
    if (this.activeTab !== newStatus) {
      this.activeTab = newStatus;
      if (this.selectedDocument) {
        this.closeDetails();
      }
      this.loadMoreIfPossible();
    }
  }

  subscribeToDocumentUpdates(): void {
    console.log('Subscribing to document updates');
    this.documentUpdateSubscription = this.ocrHistoryService.onDocumentUpdated().subscribe({
      next: (data) => {
        if (data && data.s3Key) {
          this.onDocumentSubscriptionEvent(data.s3Key);
        }
      },
      error: (err) => {
        console.error('Error subscribing to document updates:', err);
      },
      complete: () => {
        console.log('Document update subscription completed');
      }
    });
  }

  private onDocumentSubscriptionEvent(s3Key: string): void {
    this.ocrHistoryService.getDocumentByS3Key(s3Key).subscribe({
      next: (data) => {
        const doc = data?.getDocument;
        if (!doc) return;
        const mapped = this.mapToDocumentMetadata(doc);
        const currentStatus = mapped.status as DocumentStatus;
        for (const s of STATUSES) {
          const tab = this.tabs[s];
          const idx = tab.items.findIndex(d => d.s3Key === mapped.s3Key);
          if (s === currentStatus) {
            tab.items = idx >= 0
              ? tab.items.map((d, i) => i === idx ? mapped : d)
              : [mapped, ...tab.items];
          } else if (idx >= 0) {
            tab.items = tab.items.filter(d => d.s3Key !== mapped.s3Key);
          }
          tab.filtered = this.applyFilters(tab.items);
        }
        if (this.selectedDocument && this.selectedDocument.s3Key === s3Key) {
          // Enrich with processing metadata (signed URL) so the viewer updates immediately
          this.documentTemplateService.getProcessingMetadata(s3Key).subscribe({
            next: (meta) => {
              this.selectedDocument = meta;
              if (meta?.status) {
                this.activeTab = meta.status as DocumentStatus;
              }
            },
            error: () => {
              this.selectedDocument = mapped;
              if (mapped.status) {
                this.activeTab = mapped.status as DocumentStatus;
              }
            }
          });
        }
      },
      error: (err) => {
        console.error('Error fetching updated document:', err);
      }
    });
  }

  selectDocument(document: DocumentMetadata): void {
    if (this.selectedDocument && this.selectedDocument.s3Key === document.s3Key) {
      this.isCollapsed = !this.isCollapsed;
      if (!this.isCollapsed) {
        this.selectedDocument = null;
        this.router.navigate([], {
          relativeTo: this.route,
          queryParams: { s3Key: null },
          queryParamsHandling: 'merge'
        });
      }
    } else {
      this.selectedDocument = document;
      if (document.status) {
        this.activeTab = document.status as DocumentStatus;
      }
      this.isCollapsed = true;

      this.documentTemplateService.getProcessingMetadata(document.s3Key).subscribe({
        next: (meta) => {
          this.selectedDocument = { ...document, ...meta };
          if (meta?.status) {
            this.activeTab = meta.status as DocumentStatus;
          }
        },
        error: (err) => console.error('Error fetching processing metadata:', err)
      });

      this.router.navigate([], {
        relativeTo: this.route,
        queryParams: { s3Key: document.s3Key },
        queryParamsHandling: 'merge'
      });
    }
  }

  closeDetails(): void {
    this.selectedDocument = null;
    this.isCollapsed = false;

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { s3Key: null },
      queryParamsHandling: 'merge'
    });
  }

  getExtractedFieldCount(doc: DocumentMetadata): number | null {
    const res = doc?.ocrResult as any;
    if (!res) return null;
    try {
      if (Array.isArray(res)) {
        return res.length;
      }
      if (typeof res === 'object') {
        return Object.keys(res).length;
      }
    } catch {}
    return null;
  }

  /**
   * Short, human-readable error summary for the list row. Template-selection
   * failures carry a JSON payload; render its message + outcome instead of
   * dumping the raw JSON. The full breakdown is shown in the document viewer.
   */
  getShortErrorMessage(doc: DocumentMetadata): string {
    const raw = doc?.errorMessage;
    if (!raw) return 'Processing failed';
    const parsed = parseOcrError(raw);
    if (parsed.kind === 'template-mismatch') {
      return parsed.outcome ? `${parsed.message} (${parsed.outcome})` : parsed.message;
    }
    return parsed.message;
  }


  getTemplateColor(templateId: string | null | undefined): string | null {
    if (!templateId) return null;
    const template = this.store.templatesMap()[templateId];
    if (!template?.fieldsStructureId) return null;
    const structure = this.store.structuresMap()[template.fieldsStructureId];
    return structure?.color || null;
  }

  getTemplateName(templateId: string | null | undefined): string | null {
    if (!templateId) return null;
    const template = this.store.templatesMap()[templateId];
    return template?.name || null;
  }

  onFiltersChanged(): void {
    this.recomputeFiltered();
  }

  onChildFiltersChange(newFilters: typeof this.filters): void {
    this.filters = { ...newFilters };
    this.recomputeFiltered();
  }

  activeFilterCount(): number {
    const f = this.filters;
    let count = 0;
    if (f.filename && f.filename.trim()) count++;
    if (f.startDate) count++;
    if (f.endDate) count++;
    if (f.needsAttention) count++;
    return count;
  }

  resetFilters(): void {
    this.filters = {
      filename: '',
      startDate: null,
      endDate: null,
      needsAttention: false
    };
    this.recomputeFiltered();
  }

  private recomputeFiltered(): void {
    for (const s of STATUSES) {
      const tab = this.tabs[s];
      tab.filtered = this.applyFilters(tab.items);
    }
    // After recomputing, if more data is needed to fill the screen, trigger load
    this.loadMoreIfPossible();
  }

  private applyFilters(items: DocumentMetadata[]): DocumentMetadata[] {
    const f = this.filters;
    const norm = (v: string | null | undefined) => (v ?? '').toString().toLowerCase();
    const inRange = (ts: string) => {
      if (!ts) return false;
      const d = new Date(ts);
      if (isNaN(d.getTime())) return false;
      if (f.startDate) {
        const isDateOnly = /^\d{4}-\d{2}-\d{2}$/.test(f.startDate);
        const s = new Date(f.startDate);
        if (isDateOnly) {
          s.setHours(0, 0, 0, 0);
        }
        if (d < s) return false;
      }
      if (f.endDate) {
        const isDateOnly = /^\d{4}-\d{2}-\d{2}$/.test(f.endDate);
        const e = new Date(f.endDate);
        if (isDateOnly) {
          e.setHours(23, 59, 59, 999);
        }
        if (d > e) return false;
      }
      return true;
    };

    return items.filter(doc => {
      if (f.filename && !norm(doc.originalFileName).includes(norm(f.filename))) return false;
      if (f.startDate || f.endDate) {
        if (!inRange(doc.timestamp)) return false;
      }
      if (f.needsAttention) {
        const verdict = doc.ocrJudgeResult?.overallVerdict;
        const notReviewed = !doc.reviewed;
        const needsAttention = notReviewed && (verdict === 'needs_review' || verdict === 'fail');
        if (!needsAttention) return false;
      }
      return true;
    });
  }

  relativeTime(timestamp: string): string {
    if (!timestamp) return '';
    const now = new Date();
    const then = new Date(timestamp);
    const diffMs = now.getTime() - then.getTime();
    if (isNaN(diffMs)) return '';

    const sec = Math.max(1, Math.floor(diffMs / 1000));
    const min = Math.floor(sec / 60);
    const hr = Math.floor(min / 60);
    const day = Math.floor(hr / 24);
    const wk = Math.floor(day / 7);
    const mo = Math.floor(day / 30);
    const yr = Math.floor(day / 365);

    if (sec < 60) return `${sec}s ago`;
    if (min < 60) return `${min} min${min === 1 ? '' : 's'} ago`;
    if (hr < 24) return `${hr} hour${hr === 1 ? '' : 's'} ago`;
    if (day < 7) return `${day} day${day === 1 ? '' : 's'} ago`;
    if (wk < 5) return `${wk} week${wk === 1 ? '' : 's'} ago`;
    if (mo < 12) return `${mo} month${mo === 1 ? '' : 's'} ago`;
    return `${yr} year${yr === 1 ? '' : 's'} ago`;
  }

  formatFullTimestamp(timestamp: string): string {
    try {
      const d = new Date(timestamp);
      return isNaN(d.getTime()) ? timestamp : d.toLocaleString();
    } catch {
      return timestamp;
    }
  }

  onContainerFocus(): void {
    // This method ensures the container can receive focus for keyboard events
  }

  onKeyDown(event: KeyboardEvent): void {
    const filtered = this.activeTabState.filtered;
    if (!filtered.length || !this.selectedDocument) {
      return;
    }

    if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
      event.preventDefault();

      const currentIndex = filtered.findIndex(
        doc => doc.s3Key === this.selectedDocument!.s3Key
      );

      if (currentIndex === -1) {
        return;
      }

      let newIndex: number;
      if (event.key === 'ArrowUp') {
        newIndex = currentIndex === 0 ? filtered.length - 1 : currentIndex - 1;
      } else {
        newIndex = currentIndex === filtered.length - 1 ? 0 : currentIndex + 1;
      }

      this.selectDocument(filtered[newIndex]);
    }
  }
}
